import { useEffect, useState } from "react";
import { FileText, Download, Inbox as InboxIcon, Building2, User as UserIcon, Loader2 } from "lucide-react";

// "Files from your advisor" — investor surface for downloadable banker-uploaded
// artifacts. Lives alongside the existing Documents tab (which holds
// agreements/ROFR/tax). Banker artifacts get their own surface so the mental
// model stays clean: this tab = "what my advisor sent me in response to my
// asks", Documents tab = "legal paperwork I need to keep".
//
// Empty state vs missing — every artifact here corresponds to a task the
// investor raised, so the empty state copy points back to the inbox rather
// than asking the user to chase the advisor.

const BASE = import.meta.env.BASE_URL;

interface BankerArtifact {
  id: number;
  title: string;
  kind: string;
  fileName: string | null;
  fileSize: number | null;
  mimeType: string | null;
  summary: string | null;
  createdAt: string;
  bankerName: string | null;
  taskId: number | null;
  taskTitle: string | null;
  companyName: string | null;
}

function fmtBytes(n: number | null): string {
  if (!n || n <= 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(s: string): string {
  try {
    return new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch { return s; }
}

export default function InvestorPortalFilesFromBanker() {
  const [arts, setArts] = useState<BankerArtifact[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem("investor_token");
    if (!token) { setError("Please log in again."); return; }
    fetch(`${BASE}api/investor-portal/banker-artifacts`, { headers: { "x-investor-token": token } })
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || "Could not load");
        return r.json();
      })
      .then((rows: BankerArtifact[]) => setArts(rows))
      .catch((e) => { setError(e.message); setArts([]); });
  }, []);

  // Server accepts `?token=` as an alternative to the header so plain anchor
  // clicks work (browsers can't add custom headers on navigation). Using a
  // top-level navigation also means the browser does the GCS redirect and
  // download for us — no fetch-follow hack, and downloads land in the
  // user's Downloads folder with the right filename.
  const downloadHref = (id: number): string => {
    const token = localStorage.getItem("investor_token") || "";
    return `${BASE}api/investor-portal/banker-artifacts/${id}/download?token=${encodeURIComponent(token)}`;
  };

  if (arts === null) {
    return (
      <div className="flex items-center justify-center py-16 text-white/50 text-sm">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading your files...
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white font-serif">Files from your advisor</h1>
        <p className="text-white/50 text-sm mt-1">
          Files the Favcy Advisors desk has uploaded in response to your queries. Each row links back to the original request in your inbox.
        </p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/25 rounded p-3 text-red-400 text-sm mb-4">{error}</div>
      )}

      {arts.length === 0 ? (
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-10 text-center">
          <InboxIcon className="w-10 h-10 text-white/30 mx-auto mb-3" />
          <p className="text-white/70 text-sm">No files yet.</p>
          <p className="text-white/40 text-xs mt-1">When your advisor uploads a file against one of your queries, it will appear here. You'll also get an email so you don't have to check the portal.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {arts.map((a) => (
            <div key={a.id} data-testid={`banker-artifact-${a.id}`}
              className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-5 flex items-start gap-4 hover:bg-white/[0.05] transition">
              <div className="w-10 h-10 rounded-lg bg-[#FF5C00]/15 border border-[#FF5C00]/30 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-[#FF5C00]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm">{a.title}</p>
                <div className="mt-1 flex items-center gap-3 flex-wrap text-xs text-white/50">
                  {a.companyName && (<span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {a.companyName}</span>)}
                  {a.bankerName && (<span className="flex items-center gap-1"><UserIcon className="w-3 h-3" /> {a.bankerName}</span>)}
                  <span>{fmtDate(a.createdAt)}</span>
                  {a.fileName && (<span className="font-mono text-white/40 truncate max-w-[260px]">{a.fileName}{a.fileSize ? ` · ${fmtBytes(a.fileSize)}` : ""}</span>)}
                </div>
                {a.taskTitle && (
                  <p className="text-white/40 text-xs mt-2">In response to: <span className="text-white/60">{a.taskTitle}</span></p>
                )}
              </div>
              <a href={downloadHref(a.id)} data-testid={`download-banker-artifact-${a.id}`}
                className="bg-[#FF5C00] hover:bg-[#E55200] text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 flex-shrink-0">
                <Download className="w-4 h-4" /> Download
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
